import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  DialogRef,
  DialogService,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import { Enums, Hunt, Mitre } from '@cyborg-monorepo/hunter/common';
import { HuntService } from '../../shared/services/hunt.service';
import { ArchiveHuntDialogComponent } from '../../hunt/overview/archive-hunt-dialog/archive-hunt-dialog.component';
import { EditHuntDialogComponent } from '../../hunt/hunt/edit-hunt-dialog/edit-hunt-dialog.component';
import { HuntContextService } from '../../../pages/hunt/services/hunt-context.service';
import { CommonModule } from '@angular/common';
import { BadgeComponent, BreadcrumbItem, BreadcrumbListComponent, ButtonComponent, ErrorComponent, TagComponent, TypographyComponent } from '@hyperkit';
import { HuntEditorComponent } from '../../../pages/hunt/details/components/hunt-editor';
import { ReportSectionComponent, Section } from '../report-section/report-section.component';
import { filter } from 'rxjs';

@Component({ standalone: true, 
  selector: 'hunt-edit-report',
  imports: [CommonModule, RouterModule, ButtonComponent, TypographyComponent, HuntEditorComponent, BreadcrumbListComponent, BadgeComponent, TagComponent, ErrorComponent, ReportSectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hunt-edit-report.component.html',
  styleUrls: ['hunt-edit-report.component.scss'],
})
export class HuntEditReportComponent implements OnInit {
  readonly huntContext = inject(HuntContextService);
  readonly cdr = inject(ChangeDetectorRef); 
  readonly huntService = inject(HuntService); 
  readonly dialogService = inject(DialogService); 
  readonly snackBarService = inject(MatSnackBar); 
  readonly router = inject(Router); 
  readonly route = inject(ActivatedRoute);
  public breadcrumbs = signal<BreadcrumbItem[]>([]);  // Initialize with empty array

  constructor() {

  const resolvedHunt = this.route.snapshot.data['hunt'] as Hunt | null;

    if (resolvedHunt) {
      this.huntContext.state.updateHunt(resolvedHunt);
      this.huntContext.editor.load(resolvedHunt);    
    }

    effect(() => {
      this.breadcrumbs.set([
        { text: 'Hunt', selected: false, link: '/hunt/hunts' },
        { text: this.huntContext.state.name(), selected: true },
      ] as BreadcrumbItem[]); 
      });
  }

  @Input() public hunt: Hunt;
  // @Output() public updatedHunt = new EventEmitter<Hunt>();

  public priority = Enums.PriorityEnum;
  public status = Enums.StatusEnum;
  public outcome = Enums.OutcomeEnum;
  public outcomes = Object.values(Enums.OutcomeEnum);
  public form: FormGroup;
  public error: string;
  public selectedSection: 'overview' | 'hunt-package' | 'conclusion' = 'overview';
  // public sections: Section[] = [];
  public selectedSectionId: string = '';

  public outcomeMap = new Map<string, string>([
    [Enums.OutcomeEnum.DETECTION, 'Suggested Detection'],
    [Enums.OutcomeEnum.NO_FINDINGS, 'Hunt Methodology'],
    [Enums.OutcomeEnum.ESCALATED, 'Escalation Details'],
    [
      Enums.OutcomeEnum.VISIBILITY_GAP,
      'Hunt Limitations/Visibility Improvements',
    ],
  ]);

  readonly sections = computed(() => {
    console.log('Recalculating sections');

    return [
      {
        id: 'overview',
        title: 'Overview',
        status: this.huntContext.editor.reportOverviewStatus(),
      },
      {
        id: 'technical-summary',
        title: 'Technical Summary',
        children: [
          ...this.huntContext.query.huntPackages().map((huntPackage) => ({
            id: huntPackage.UUID,
            title: huntPackage.title,
            status: Enums.StatusEnum.IN_PROGRESS
          }))
        ]
      },
      {
        id: 'conclusion',
        title: 'Conclusion',
        status: Enums.StatusEnum.DONE
      }
    ];
  });


  public outcomeDependentText: string = 'Outcome Description';

  public ngOnInit(): void {
    const urlPath = this.router.url;
    this.selectedSectionId = urlPath.split('/').pop() || '';
    

    // Initialize the sections
    // this.sections = [
    //     {
    //         id: 'overview',
    //         title: 'Overview',
    //         status: this.huntContext.editor.reportOverviewStatus(),
    //     },
    //     {
    //         id: 'technical-summary',
    //         title: 'Technical Summary',
    //         children: [
    //             ...huntPackages.map((huntPackage) => ({
    //                 id: huntPackage.UUID, 
    //                 title: huntPackage.title, 
    //                 status: Enums.StatusEnum.IN_PROGRESS
    //             }))
    //         ]
    //     },
    //     {
    //         id: 'conclusion',
    //         title: 'Conclusion',
    //         status: Enums.StatusEnum.DONE
    //     }
    // ];

}


  onSelectSection(sectionId: string): void {
    this.router.navigate([sectionId], { relativeTo: this.route });
    this.selectedSectionId = sectionId;
  }

  // updateSectionStatus(sectionId: string, status: Enums.StatusEnum): void {
  //   // Helper function to recursively find and update a section
  //   const updateStatus = (sections: Section[]): boolean => {
  //     for (let section of sections) {
  //       if (section.id === sectionId) {
  //         section.status = status;
  //         return true;
  //       }
        
  //       if (section.children && section.children.length > 0) {
  //         if (updateStatus(section.children)) {
  //           return true;
  //         }
  //       }
  //     }
  //     return false;
  //   };
    
  //   updateStatus(this.sections);
  // }


  public editHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      EditHuntDialogComponent,
      { data: this.hunt },
    );

    dialogRef.afterClosed().subscribe((updatedHunt: Hunt) => {
      if (updatedHunt) this.hunt = updatedHunt;
    });
  }

  public onSubmit(): void {
    this.huntService.finalizeHuntReport(this.hunt.id).subscribe((hunt) => {
      this.hunt = hunt;
      // this.updatedHunt.emit(this.hunt);
    });
  }

  public reopenHunt(): void {
    this.huntService
      .updateHuntStatus(this.hunt.id, {
        status: Enums.StatusEnum.IN_PROGRESS,
      })
      .subscribe((result) => {
        this.hunt = result;
        // this.updatedHunt.emit(this.hunt);
      });
  }

  public archiveHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      ArchiveHuntDialogComponent,
      { data: this.hunt },
    );

    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['/hunt/hunts']);
        this.snackBarService.open('Archived ' + hunt.name, null, {
          duration: 5 * 1000,
        });
      }
    });
  }

  public getMitres(mitres: Mitre): { id: string; technique: string }[] {
    const generatedMitres: { id: string; technique: string }[] = [];
    for (let i = 0; i < mitres.mitre_attack_techniques.length; i += 1) {
      const mitre = { technique: '', id: '' };
      mitre.technique = mitres.mitre_attack_techniques[i];
      mitre.id = mitres.mitre_attack_technique_ids[i];
      generatedMitres.push(mitre);
    }
    return generatedMitres;
  }

  public backToHunt(): void {
        this.huntContext.editor.updateStatus(Enums.StatusEnum.IN_PROGRESS);
        this.huntContext.editor.updateEditing(true);
        this.huntContext.editor.submit();
        this.router.navigate(['hunt-redesign', this.huntContext.state.id(), 'workbench']);
  }

  public done(): void {
    console.log("done");
    this.huntContext.editor.updateStatus(Enums.StatusEnum.DONE);
    this.huntContext.editor.updateEditing(false);
    this.huntContext.editor.submit();
    this.router.navigate(['hunt-redesign', this.huntContext.state.id(), 'final-report']);
}

  public updateSelectedSection(section: 'overview' | 'hunt-package' | 'conclusion') {
    this.selectedSection = section;
    this.cdr.detectChanges();
  }
}
